import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';
import { Plus, Settings, Users, Globe, Check, X, Eye, EyeOff, Trash2 } from 'lucide-react';

const Domains = () => {
  const [domainData, setDomainData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [addEmailForm, setAddEmailForm] = useState({
    email: '',
    isp: 'MS',
    super_admin_id: ''
  });
  const [superAdmins, setSuperAdmins] = useState([]);
  const [superAdminDomains, setSuperAdminDomains] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Super Admin management states
  const [showAddSuperAdminModal, setShowAddSuperAdminModal] = useState(false);
  const [showAddDomainModal, setShowAddDomainModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('domains'); // 'domains' or 'super-admins'
  
  const [superAdminFormData, setSuperAdminFormData] = useState({
    email: '',
    name: '',
    provider: 'google',
    google_service_account_json: '',
    outlook_client_id: '',
    outlook_tenant_id: '',
    outlook_client_secret: ''
  });

  const [domainFormData, setDomainFormData] = useState({
    name: '',
    super_admin_id: ''
  });

  useEffect(() => {
    fetchDomains();
    fetchSuperAdmins();
    fetchSuperAdminDomains();
  }, []);

  const fetchSuperAdminDomains = async () => {
    try {
      // Use clean MongoDB API endpoint
      const response = await fetch('/api/v1/super-admin/domains');
      const data = await response.json();
      setSuperAdminDomains(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load super admin domains:', error);
      setSuperAdminDomains([]);
    }
  };

  const fetchSuperAdmins = async () => {
    try {
      // Use clean MongoDB API endpoint
      const response = await fetch('/api/v1/super-admin/');
      const data = await response.json();
      setSuperAdmins(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load super admins:', error);
      setSuperAdmins([]);
    }
  };

  // Super Admin management functions
  const handleCreateSuperAdmin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/v1/super-admin/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(superAdminFormData),
      });

      if (response.ok) {
        const newSuperAdmin = await response.json();
        setSuperAdmins([...superAdmins, newSuperAdmin]);
        setShowAddSuperAdminModal(false);
        setSuperAdminFormData({
          email: '',
          name: '',
          provider: 'google',
          google_service_account_json: '',
          outlook_client_id: '',
          outlook_tenant_id: '',
          outlook_client_secret: ''
        });
        toast.success('Super Admin created successfully!');
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error creating super admin:', error);
      toast.error('Failed to create Super Admin');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateDomain = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/v1/super-admin/domains', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(domainFormData),
      });

      if (response.ok) {
        const newDomain = await response.json();
        setShowAddDomainModal(false);
        setDomainFormData({ name: '', super_admin_id: '' });
        toast.success('Domain created successfully!');
        // Refresh both domains and super admins to update counts
        await fetchDomains();
        await fetchSuperAdmins();
        await fetchSuperAdminDomains();
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error creating domain:', error);
      toast.error('Failed to create domain');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    setIsDeleting(true);
    try {
      const endpoint = itemToDelete.type === 'super-admin' 
        ? `/api/v1/super-admin/${itemToDelete.id}`
        : `/api/v1/super-admin/domains/${itemToDelete.id}`;
        
      const response = await fetch(endpoint, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success(`${itemToDelete.type === 'super-admin' ? 'Super Admin' : 'Domain'} deleted successfully!`);
        setShowDeleteConfirmModal(false);
        setItemToDelete(null);
        // Refresh data
        await fetchDomains();
        await fetchSuperAdmins();
        await fetchSuperAdminDomains();
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete item');
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDelete = (item, type) => {
    setItemToDelete({ ...item, type });
    setShowDeleteConfirmModal(true);
  };

  // Validate JSON format
  const validateJSON = (jsonString) => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch {
      return false;
    }
  };

  const fetchDomains = async () => {
    try {
      const response = await apiService.getDomains();
      console.log('Domains response:', response); // Debug log
      
      // Handle the actual API response structure
      if (response && response.data) {
        // Handle nested response structure
        let apiData;
        if (response.data.success && response.data.data) {
          apiData = response.data.data;
        } else {
          apiData = response.data;
        }
        
        // API already returns domains as array, use directly
        const processedData = {
          domains: apiData.domains || [],
          all_accounts: apiData.all_accounts || [],
          total_domains: apiData.total_domains || 0,
          total_accounts: apiData.total_accounts || 0
        };
        
        console.log('Processed domains:', processedData); // Debug log
        setDomainData(processedData);
      }
    } catch (error) {
      console.error('Failed to load domains:', error);
      toast.error('Failed to load domain information');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Extract domain from email
      const domain = addEmailForm.email.split('@')[1];
      
      // Create email account using Super Admin API
      const emailAccountData = {
        username: addEmailForm.email,
        domain: domain,
        isp: addEmailForm.isp,
        super_admin_id: parseInt(addEmailForm.super_admin_id)
      };

      const response = await fetch('/api/v1/super-admin/email-accounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailAccountData),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Email account added successfully!');
        setShowAddModal(false);
        setAddEmailForm({ email: '', isp: 'MS', super_admin_id: '' });
        
        // Refresh domains list and super admin counts
        await fetchDomains();
        await fetchSuperAdmins();
        await fetchSuperAdminDomains();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add email account');
      }
    } catch (error) {
      console.error('Failed to add email:', error);
      toast.error('Failed to add email account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormChange = (field, value) => {
    setAddEmailForm(prev => {
      const newForm = {
        ...prev,
        [field]: value
      };

      // Auto-select matching super admin when ISP changes
      if (field === 'isp') {
        const matchingAdmin = superAdmins.find(admin => {
          if (value === 'MS') {
            return admin.provider === 'outlook';
          } else if (value === 'GCP') {
            return admin.provider === 'google';
          }
          return false;
        });
        
        if (matchingAdmin) {
          newForm.super_admin_id = matchingAdmin.id;
        } else {
          newForm.super_admin_id = '';
        }
      }

      // Auto-select matching super admin when email changes (based on domain)
      if (field === 'email' && value.includes('@')) {
        const emailDomain = value.split('@')[1];
        
        // First, try to find admin with exact email match
        let matchingAdmin = superAdmins.find(admin => admin.email === value);
        
        // If no exact match, try domain matching with ISP compatibility
        if (!matchingAdmin) {
          matchingAdmin = superAdmins.find(admin => {
            const adminDomain = admin.email.split('@')[1];
            const ispMatches = (newForm.isp === 'MS' && admin.provider === 'outlook') ||
                             (newForm.isp === 'GCP' && admin.provider === 'google');
            return adminDomain === emailDomain && ispMatches;
          });
        }
        
        // If still no match, find any admin with matching ISP
        if (!matchingAdmin) {
          matchingAdmin = superAdmins.find(admin => {
            return (newForm.isp === 'MS' && admin.provider === 'outlook') ||
                   (newForm.isp === 'GCP' && admin.provider === 'google');
          });
        }
        
        if (matchingAdmin) {
          newForm.super_admin_id = matchingAdmin.id;
        }
      }

      return newForm;
    });
  };

  const handleDeleteEmail = async () => {
    if (!emailToDelete) return;
    
    setIsDeleting(true);
    try {
      const response = await apiService.deleteEmail(emailToDelete);
      
      if (response && response.success) {
        toast.success(response.message || 'Email deleted successfully!');
        setShowDeleteModal(false);
        setEmailToDelete(null);
        
        // Refresh domains list
        await fetchDomains();
      } else {
        toast.error(response.message || 'Failed to delete email');
      }
    } catch (error) {
      console.error('Delete email error:', error);
      toast.error('Failed to delete email account');
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDeleteEmail = (email) => {
    setEmailToDelete(email);
    setShowDeleteModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner mr-2"></div>
        <span>Loading domains...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in pt-16">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Domain & Super Admin Management</h1>
          <p className="text-gray-500">Manage email provider credentials, domains, and email accounts</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('domains')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'domains'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Globe className="inline-block w-4 h-4 mr-2" />
            Domains ({superAdminDomains.length + (domainData?.total_domains || 0)})
          </button>
          <button
            onClick={() => setActiveTab('super-admins')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'super-admins'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Settings className="inline-block w-4 h-4 mr-2" />
            Super Admins ({superAdmins.length})
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'domains' && (
        <>
          {/* Domain Management Actions */}
          <div className="flex space-x-3">
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              <Plus size={20} />
              <span>Add Email</span>
            </button>
            <button
              onClick={() => setShowAddDomainModal(true)}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              <Globe size={20} />
              <span>Add Domain</span>
            </button>
            <button onClick={() => fetchDomains()} className="btn-primary">
              Refresh Domains
            </button>
          </div>
        </>
      )}

      {activeTab === 'super-admins' && (
        <>
          {/* Super Admin Management Actions */}
          <div className="flex space-x-3">
            <button
              onClick={() => setShowAddSuperAdminModal(true)}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              <Plus size={20} />
              <span>Add Super Admin</span>
            </button>
            <button onClick={() => fetchSuperAdmins()} className="btn-primary">
              Refresh Super Admins
            </button>
          </div>
        </>
      )}

      {/* Domains Tab Content */}
      {activeTab === 'domains' && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="metric-card">
          <h3>Total Domains</h3>
          <p>{domainData?.total_domains || 0}</p>
        </div>
        <div className="metric-card">
          <h3>Total Accounts</h3>
          <p>{domainData?.total_accounts || 0}</p>
        </div>
        <div className="metric-card">
          <h3>ISP Distribution</h3>
          <div className="text-sm text-gray-600 mt-2">
            {domainData && domainData.all_accounts && (() => {
              const ispCounts = domainData.all_accounts.reduce((acc, account) => {
                acc[account.isp] = (acc[account.isp] || 0) + 1;
                return acc;
              }, {});
              return Object.entries(ispCounts).map(([isp, count]) => (
                <div key={isp}>{isp}: {count}</div>
              ));
            })()}
          </div>
        </div>
      </div>

      {/* Domains List */}
      <div className="space-y-4">
        {/* Super Admin Domains */}
        {superAdminDomains.map((domainInfo) => {
          const superAdmin = superAdmins.find(sa => sa.id === domainInfo.super_admin_id);
          return (
            <div key={`sa-${domainInfo.id}`} className="bg-white rounded-lg p-6 card-shadow border-l-4 border-blue-500">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{domainInfo.name}</h3>
                  <p className="text-sm text-gray-500">
                    Super Admin: {superAdmin ? superAdmin.name : 'Unknown'} • {domainInfo.email_account_count} accounts
                  </p>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                    <Settings size={12} className="mr-1" />
                    Super Admin Managed
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="status-badge status-success">
                    {domainInfo.email_account_count} Accounts
                  </span>
                  <button className="btn-secondary text-sm">Manage</button>
                  <button
                    onClick={() => confirmDelete({ id: domainInfo.id, name: domainInfo.name }, 'domain')}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                    title="Delete Domain"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {/* Legacy Domains (from marketing API) */}
        {domainData && domainData.domains && domainData.domains.map((domainInfo) => {
          const domainAccounts = domainData.all_accounts.filter(acc => acc.domain === domainInfo.name);
          return (
            <div key={`legacy-${domainInfo.name}`} className="bg-white rounded-lg p-6 card-shadow border-l-4 border-gray-300">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{domainInfo.name}</h3>
                  <p className="text-sm text-gray-500">
                    {domainInfo.account_count} accounts • {domainAccounts[0]?.isp} ISP
                  </p>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mt-1">
                    Legacy Domain
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="status-badge status-success">
                    {domainAccounts.filter(acc => acc.is_active).length} Active
                  </span>
                  <button className="btn-secondary text-sm">Manage</button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {domainAccounts.slice(0, 6).map((account) => (
                  <div key={account.email} className="p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{account.email}</div>
                        <div className="text-xs text-gray-500">
                          {account.isp} ISP
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${
                          account.is_active ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                        <button
                          onClick={() => confirmDeleteEmail(account.email)}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title="Delete Email"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {domainAccounts.length > 6 && (
                  <div className="p-3 bg-gray-100 rounded-md flex items-center justify-center">
                    <span className="text-sm text-gray-600">+{domainAccounts.length - 6} more</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        
        {domainData && (!domainData.domains || domainData.domains.length === 0) && (
          <div className="text-center py-12">
            <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9 3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No domains configured</h3>
            <p className="text-gray-500 mb-4">Add email accounts to get started with domain management</p>
            <button className="btn-primary">Add Domain</button>
          </div>
        )}
      </div>
        </>
      )}

      {/* Super Admins Tab Content */}
      {activeTab === 'super-admins' && (
        <>
          {/* Super Admins List */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-4">
              {superAdmins.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No Super Admins found. Add one to get started.</p>
              ) : (
                <div className="grid gap-4">
                  {superAdmins.map((admin) => (
                    <div key={admin.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-lg">{admin.name}</h3>
                          <p className="text-gray-600">{admin.email}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className={`px-2 py-1 rounded text-sm ${
                              admin.provider === 'google' 
                                ? 'bg-red-100 text-red-800' 
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {admin.provider === 'google' ? 'Google' : 'Outlook'}
                            </span>
                            <span className={`px-2 py-1 rounded text-sm ${
                              admin.is_verified 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {admin.is_verified ? 'Verified' : 'Pending'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <div className="text-sm text-gray-500">
                              {admin.domain_count} domains • {admin.email_account_count} accounts
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              Created: {new Date(admin.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <button
                            onClick={() => confirmDelete(admin, 'super-admin')}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            title="Delete Super Admin"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Add Email Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add New Email Account</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleAddEmail} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={addEmailForm.email}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    placeholder="user@domain.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label htmlFor="isp" className="block text-sm font-medium text-gray-700 mb-1">
                    ISP Provider *
                  </label>
                  <select
                    id="isp"
                    value={addEmailForm.isp}
                    onChange={(e) => handleFormChange('isp', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    disabled={isSubmitting}
                  >
                    <option value="MS">Microsoft (MS)</option>
                    <option value="GCP">Google Cloud (GCP)</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="super_admin" className="block text-sm font-medium text-gray-700 mb-1">
                    Super Admin * 
                    <span className="text-xs text-gray-500 ml-2">(Auto-selected based on ISP and domain)</span>
                  </label>
                  <select
                    id="super_admin"
                    value={addEmailForm.super_admin_id}
                    onChange={(e) => handleFormChange('super_admin_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select Super Admin</option>
                    {superAdmins.map((admin) => {
                      const isCompatible = (addEmailForm.isp === 'MS' && admin.provider === 'outlook') ||
                                          (addEmailForm.isp === 'GCP' && admin.provider === 'google');
                      const providerText = admin.provider === 'google' ? 'Google' : 'Outlook';
                      const compatibilityIcon = isCompatible ? '✅' : '❌';
                      
                      return (
                        <option 
                          key={admin.id} 
                          value={admin.id}
                          style={{ 
                            color: isCompatible ? '#059669' : '#DC2626',
                            fontWeight: isCompatible ? 'normal' : 'bold'
                          }}
                        >
                          {compatibilityIcon} {admin.name} ({admin.email}) - {providerText}
                        </option>
                      );
                    })}
                  </select>
                  {addEmailForm.super_admin_id && (() => {
                    const selectedAdmin = superAdmins.find(admin => admin.id === addEmailForm.super_admin_id);
                    if (selectedAdmin) {
                      const isCompatible = (addEmailForm.isp === 'MS' && selectedAdmin.provider === 'outlook') ||
                                          (addEmailForm.isp === 'GCP' && selectedAdmin.provider === 'google');
                      if (!isCompatible) {
                        return (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                            <div className="flex items-center">
                              <div className="text-red-600 text-sm">
                                ⚠️ <strong>Provider Mismatch:</strong> You selected {addEmailForm.isp} ISP but {selectedAdmin.name} is a {selectedAdmin.provider === 'google' ? 'Google' : 'Outlook'} admin.
                              </div>
                            </div>
                          </div>
                        );
                      }
                    }
                    return null;
                  })()}
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center">
                        <div className="loading-spinner mr-2"></div>
                        Adding...
                      </div>
                    ) : (
                      'Add Email'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Email Confirmation Modal */}
      {showDeleteModal && emailToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-red-600">Delete Email Account</h3>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Are you sure?</h4>
                    <p className="text-sm text-gray-500">This will permanently delete the email account.</p>
                  </div>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                  <div className="flex">
                    <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm">
                      <p className="font-medium text-yellow-800">Email account will be deleted from:</p>
                      <ul className="mt-1 list-disc list-inside text-yellow-700">
                        <li>Database (account settings)</li>
                        <li>CSV file (User/username.csv)</li>
                      </ul>
                      <p className="mt-2 text-yellow-800">✅ <strong>Email logs will be preserved</strong> for historical data.</p>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-sm font-medium text-gray-900 mb-1">Delete Account:</p>
                  <p className="text-lg font-mono text-red-600 bg-red-50 px-3 py-2 rounded border">
                    {emailToDelete}
                  </p>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteEmail}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isDeleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </>
                  ) : (
                    'Delete Account'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Super Admin Modal */}
      {showAddSuperAdminModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Add Super Admin</h2>
              <button
                onClick={() => setShowAddSuperAdminModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateSuperAdmin} className="space-y-4">
              {/* Basic Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={superAdminFormData.email}
                  onChange={(e) => setSuperAdminFormData({ ...superAdminFormData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="admin@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={superAdminFormData.name}
                  onChange={(e) => setSuperAdminFormData({ ...superAdminFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Admin Name"
                />
              </div>

              {/* Provider Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Provider *
                </label>
                <select
                  value={superAdminFormData.provider}
                  onChange={(e) => setSuperAdminFormData({ ...superAdminFormData, provider: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="google">Google (Gmail)</option>
                  <option value="outlook">Microsoft (Outlook)</option>
                </select>
              </div>

              {/* Google Credentials */}
              {superAdminFormData.provider === 'google' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Account JSON *
                  </label>
                  <textarea
                    required
                    value={superAdminFormData.google_service_account_json}
                    onChange={(e) => setSuperAdminFormData({ ...superAdminFormData, google_service_account_json: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm ${
                      superAdminFormData.google_service_account_json && !validateJSON(superAdminFormData.google_service_account_json)
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                    }`}
                    rows="8"
                    placeholder='{"type": "service_account", "project_id": "...", ...}'
                  />
                  {superAdminFormData.google_service_account_json && !validateJSON(superAdminFormData.google_service_account_json) && (
                    <p className="text-red-500 text-sm mt-1">Invalid JSON format</p>
                  )}
                </div>
              )}

              {/* Outlook Credentials */}
              {superAdminFormData.provider === 'outlook' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client ID *
                    </label>
                    <input
                      type="text"
                      required
                      value={superAdminFormData.outlook_client_id}
                      onChange={(e) => setSuperAdminFormData({ ...superAdminFormData, outlook_client_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="12345678-1234-1234-1234-123456789012"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tenant ID *
                    </label>
                    <input
                      type="text"
                      required
                      value={superAdminFormData.outlook_tenant_id}
                      onChange={(e) => setSuperAdminFormData({ ...superAdminFormData, outlook_tenant_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="87654321-4321-4321-4321-210987654321"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Secret *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        required
                        value={superAdminFormData.outlook_client_secret}
                        onChange={(e) => setSuperAdminFormData({ ...superAdminFormData, outlook_client_secret: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Your client secret"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Submit Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddSuperAdminModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || (superAdminFormData.provider === 'google' && !validateJSON(superAdminFormData.google_service_account_json))}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      Create Super Admin
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Domain Modal */}
      {showAddDomainModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Add Domain</h2>
              <button
                onClick={() => setShowAddDomainModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateDomain} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Domain Name *
                </label>
                <input
                  type="text"
                  required
                  value={domainFormData.name}
                  onChange={(e) => setDomainFormData({ ...domainFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Super Admin *
                </label>
                <select
                  required
                  value={domainFormData.super_admin_id}
                  onChange={(e) => setDomainFormData({ ...domainFormData, super_admin_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Super Admin</option>
                  {superAdmins.map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.name} ({admin.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddDomainModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      Create Domain
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-red-600">Delete {itemToDelete.type === 'super-admin' ? 'Super Admin' : 'Domain'}</h2>
              <button
                onClick={() => setShowDeleteConfirmModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                  <Trash2 className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Are you sure?</h4>
                  <p className="text-sm text-gray-500">
                    This will permanently delete the {itemToDelete.type === 'super-admin' ? 'Super Admin and all associated domains and email accounts' : 'domain and all associated email accounts'}.
                  </p>
                </div>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <p className="text-sm font-medium text-red-800">
                  {itemToDelete.type === 'super-admin' ? 'Super Admin' : 'Domain'}: {itemToDelete.name || itemToDelete.email}
                </p>
                {itemToDelete.type === 'super-admin' && (
                  <p className="text-xs text-red-600 mt-1">
                    This will also delete {itemToDelete.domain_count} domains and {itemToDelete.email_account_count} email accounts.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirmModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete {itemToDelete.type === 'super-admin' ? 'Super Admin' : 'Domain'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Domains;
